import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart, Trash2, ArrowRight, Share2, MessageCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PropertyCard from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import { useFavorites } from '@/contexts/FavoritesContext';
import { toast } from 'sonner';

const FavoritesPage = () => {
  const { t } = useTranslation();
  const { favorites, clearFavorites, favoritesCount } = useFavorites();

  const shareViaWhatsApp = () => {
    if (favorites.length === 0) {
      toast.error(t('favorites.noFavoritesToShare', 'No tienes favoritos para compartir'));
      return;
    }
    
    const baseUrl = 'https://ikonicdeals.com';
    const WHATSAPP_NUMBER = '50242547204';
    
    // Build message with property list
    let message = `🏠 *Mis propiedades favoritas en Ikonic Deals*\n\n`;
    
    favorites.slice(0, 5).forEach((prop, index) => {
      const propertyUrl = `${baseUrl}/propiedad/${prop.slug || prop.id}`;
      message += `${index + 1}. *${prop.name}*\n`;
      message += `   📍 ${prop.zone}, ${prop.department}\n`;
      message += `   💰 $${prop.price_usd?.toLocaleString()} USD\n`;
      message += `   🔗 ${propertyUrl}\n\n`;
    });
    
    if (favorites.length > 5) {
      message += `... y ${favorites.length - 5} propiedades más.\n\n`;
    }
    
    message += `¿Te interesan estas propiedades? Contáctanos: wa.me/${WHATSAPP_NUMBER}`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success(t('favorites.sharingViaWhatsApp', 'Abriendo WhatsApp...'));
  };

  return (
    <>
      <SEO 
        title="Mis Favoritos | Ikonic Deals"
        description="Tus propiedades guardadas en Ikonic Deals"
        noindex={true}
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#0a0a0a] to-[#050505]">
        <div className="container-custom px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
                {t('favorites.subtitle', 'Tu Selección')}
              </span>
              <h1 className="mt-3 text-3xl md:text-4xl text-white flex items-center gap-3">
                <Heart className="text-red-500 fill-red-500" size={32} />
                {t('favorites.title', 'Mis Favoritos')}
              </h1>
              <p className="mt-2 text-[#A3A3A3]">
                {favoritesCount === 0 
                  ? t('favorites.empty', 'No tienes propiedades guardadas')
                  : t('favorites.count', '{{count}} propiedades guardadas', { count: favoritesCount })
                }
              </p>
            </div>
            
            {favoritesCount > 0 && (
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={shareViaWhatsApp}
                  className="bg-[#25D366] hover:bg-[#20BD5A] text-white"
                  data-testid="share-favorites-whatsapp-btn"
                >
                  <MessageCircle size={16} className="mr-2" />
                  {t('favorites.shareWhatsApp', 'Compartir por WhatsApp')}
                </Button>
                <Button
                  variant="outline"
                  onClick={clearFavorites}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                >
                  <Trash2 size={16} className="mr-2" />
                  {t('favorites.clearAll', 'Limpiar todo')}
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Favorites Grid */}
      <section className="py-12 min-h-[50vh]">
        <div className="container-custom px-4 sm:px-6">
          {favoritesCount === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <Heart size={64} className="mx-auto text-white/20 mb-6" />
              <h2 className="text-2xl text-white mb-4">
                {t('favorites.emptyTitle', 'Aún no tienes favoritos')}
              </h2>
              <p className="text-[#A3A3A3] mb-8 max-w-md mx-auto">
                {t('favorites.emptyDescription', 'Explora nuestras propiedades y guarda tus favoritas haciendo clic en el corazón')}
              </p>
              <Link to="/propiedades">
                <Button className="bg-[#D4AF37] text-black hover:bg-[#FFE182]">
                  {t('favorites.exploreProperties', 'Explorar Propiedades')}
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {favorites.map((property, index) => (
                <PropertyCard 
                  key={property.id} 
                  property={property} 
                  index={index}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
};

export default FavoritesPage;
